import { useEffect, useMemo } from 'react';
import { useAppDispatch } from '../../useAppDispatch';
import { analysisApi } from '../service';
import { skipToken } from '@reduxjs/toolkit/query';
import { useSelector } from 'react-redux';
import { SavedAnalysisReport } from '../types';

function createSavedReportsRecord(data: (SavedAnalysisReport | undefined)[]): Record<string, SavedAnalysisReport> {
    return data.reduce((record, report) => {
        if (report) {
            return {
                ...record,
                [report.url]: report,
            };
        } else {
            return record;
        }
    }, {} as Record<string, SavedAnalysisReport>);
}

type Params = {
    guids: string[];
    urls: string[];
};

/**
 * Simulate a useQuery hook that will fetch data for multiple reports at once.
 * @param guids a list of guids. Can be empty, which will skip selection
 * @param urls can be empty, which will skip selection
 * @returns a record of type url:report
 */
export default function useFetchMultipleSavedReports({ guids, urls }: Params) {
    const dispatch = useAppDispatch();

    const selectSavedReport = useMemo(
        () =>
            guids.map((_, index) =>
                analysisApi.endpoints.fetchSavedAnalysisReport.select(guids.length > 0 ? { guid: guids[index], url: urls[index] } : skipToken)
            ),
        [guids, urls]
    );

    const savedReportQueryStates = useSelector((state) => guids.map((_, index) => selectSavedReport[index](state as any)));

    /**
     * The first render of a useQuery hook masks the isUninitialized flag, even if the actual dispatching of the initiate
     * util is done in an useEffect hook after the first render. This imitates the library's behavior
     */
    const isLoadingMultipleSavedReports = useMemo(
        () => savedReportQueryStates.some((query) => query.isLoading || query.isUninitialized),
        [savedReportQueryStates]
    );

    const groupedSavedReports = useMemo(() => createSavedReportsRecord(savedReportQueryStates.map((query) => query.data)), [savedReportQueryStates]);

    useEffect(() => {
        const subscriptions = guids.map((_, index) => {
            return dispatch(analysisApi.endpoints.fetchSavedAnalysisReport.initiate({ guid: guids[index], url: urls[index] }));
        });

        return () => {
            subscriptions.forEach((subscription) => subscription.unsubscribe());
        };
    }, [dispatch, guids, urls]);

    return {
        data: groupedSavedReports,
        isLoading: isLoadingMultipleSavedReports,
    };
}
